import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { PROPERTY_OWNER_STORE_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import { propertyOwnerStoreRentSchema } from './propertyOwnerStoreRentSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import CommercialCostsRent from '../../../../../components/pages/insertionPage/screens/costs/wrappers/commercial/CommercialCostsRent'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  estateFeatures,
  flooringType,
  floorLoadField,
  frontWindowLength,
  goodsLift,
  interiorQuality,
  locationClassificationType,
  parkingSpace,
  supplyType,
  totalFloorsField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  mainFloorSpaceField,
  priceField,
  priceTypeField,
  separableSpaceField,
  totalFloorSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import { SeparableSpaceType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/separableSpaceField/SeparableSpaceField'
import { buildingTypeField } from '../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'

// Shared screen
export const storeRentBasicInfoScreen: FlowScreenElement = {
  screen: SCREENS[SCREEN_BASIC_INFO],
  element: (props) => (
    <BasicInfo
      {...props}
      screenTooltipKey='tooltip.screens.basicInfo.storeRentSell'
      properties={[
        mainFloorSpaceField(MainFloorType.STORE_AREA, 'left', undefined),
        separableSpaceField(SeparableSpaceType.STORE_AREA, 'right', undefined),
        totalFloorSpaceField(),
        buildingTypeField(
          'object',
          [
            BuildingTypeOption.SHOWROOM_SPACE,
            BuildingTypeOption.SHOPPING_CENTRE,
            BuildingTypeOption.FACTORY_OUTLET,
            BuildingTypeOption.DEPARTMENT_STORE,
            BuildingTypeOption.KIOSK,
            BuildingTypeOption.SELF_SERVICE_MARKET,
            BuildingTypeOption.SALES_AREA,
            BuildingTypeOption.SALES_HALL,
          ],
          getDefaultClasses('right')
        ),
        priceField(PriceFieldType.RENT_PRICE, getDefaultClasses('leftOnAll')),
        priceTypeField(),
      ]}
    />
  ),
}

const propertyOwnerStoreRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  storeRentBasicInfoScreen,
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <CommercialCostsRent {...props} hasMoveInDate={true} screenTooltipKey='tooltip.screens.costs.allRent' />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          estateFeatures([
            RealEstateFeatures.BASEMENT,
            RealEstateFeatures.RAMP,
            RealEstateFeatures.LISTED,
            RealEstateFeatures.ELEVATOR,
          ]),
          interiorQuality(),
          locationClassificationType(getDefaultClasses('left')),
          frontWindowLength(getDefaultClasses('right')),
          supplyType(),
          flooringType(),
          floorLoadField(getDefaultClasses('left'), 'floorLoadCeiling'),
          totalFloorsField(getDefaultClasses('right')),
          goodsLift(),
          parkingSpace({ showParkingSpaceType: false }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerStoreRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.store',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_STORE_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_STORE_RENT,
    validationSchema: propertyOwnerStoreRentSchema,
    metaData: {
      category: RealEstateCategory.STORE,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
  },
  flowScreens: propertyOwnerStoreRentFlowScreens,
}
